import { z } from 'zod';
import { queryBoolean } from './query.js';

import { COMMISSION_SOURCES } from './commission.js';

/**
 * F44 — Admin Financial Dashboard frozen API contracts.
 *
 * See plans/44_F44_admin_financial_dashboard.md (the WHAT) +
 * C:\Users\Tech\.claude\plans\let-us-plan-how-jolly-wall.md (the execution
 * meta-plan, the WHAT-supersedes corrections). Wave 2 sub-agents (2A read /
 * 2B export / 2C rollup+alerts / 2D admin web) develop against these contracts
 * in parallel — DO NOT rename, drop, or add fields without escalating to the
 * orchestrator first (contract-amendment protocol).
 *
 * F44 is READ-ONLY aggregation + export + email alerts. It writes NO financial
 * data; every settle/refund/override deep-links into the owning feature. All
 * monetary fields are USD minor units (integer cents) at MVP (D12 — `currency`
 * is the constant 'USD'; a GROUP BY currency seam is reserved for MENA).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/**
 * The 7 revenue sources F44 aggregates — EXACTLY the `commission_statements`
 * source set (D2). A drift test asserts this stays identical to
 * `COMMISSION_SOURCES`. NEVER invent an 8th (e.g. there is no
 * `tournament_ticketing_commission` — ticketing is an inert seam at MVP).
 */
export const FINANCE_REVENUE_SOURCES = COMMISSION_SOURCES;
export type FinanceRevenueSource = (typeof FINANCE_REVENUE_SOURCES)[number];

/**
 * `finance_revenue_daily.entity_kind` — the polymorphic entity dimension.
 * 'venue' (venue_id), 'trainer' (trainer_user_id), 'organizer'
 * (organizer_b2b_account_id for tournament_listing).
 */
export const FINANCE_ENTITY_KINDS = ['venue', 'trainer', 'organizer'] as const;
export type FinanceEntityKind = (typeof FINANCE_ENTITY_KINDS)[number];

/**
 * The 4 admin finance alerts (D11). EMAIL-ONLY via AdminEmailService — admins
 * have no in-app inbox (meta-plan C2). Thresholds live in `finance_alert_rules`.
 */
export const FINANCE_ALERT_TYPES = [
  'daily_finance_summary',
  'weekly_finance_digest',
  'large_refund_processed',
  'ar_aging_threshold_crossed',
] as const;
export type FinanceAlertType = (typeof FINANCE_ALERT_TYPES)[number];

/** AR-aging buckets (D9). `current` = not yet due. */
export const AR_AGING_BUCKETS = ['current', '1_30', '31_60', '61_90', '90_plus'] as const;
export type ArAgingBucket = (typeof AR_AGING_BUCKETS)[number];

/**
 * The 5 real refund/reversal sources the consolidated refunds queue UNIONs
 * (D7). Note: `promotion_reversal` is a *discount* reversal (not cash) and
 * `commission_reversal` is the platform-revenue impact of a refund/dispute;
 * `booking_refund` + `tournament_refund` are off-platform cash refunds.
 */
export const REFUND_SOURCE_KINDS = [
  'booking_refund',
  'commission_reversal',
  'placement_credit',
  'promotion_reversal',
  'tournament_refund',
] as const;
export type RefundSourceKind = (typeof REFUND_SOURCE_KINDS)[number];

/**
 * The 12 dashboard KPI cards (D2). `refunds_total`, `discount_impact`, and
 * `admin_credits` are INFORMATIONAL (shown, NOT subtracted from net — D8).
 * `admin_credits` is an additional informational card alongside the 12.
 */
export const FINANCE_KPI_KEYS = [
  'gross_revenue',
  'net_revenue',
  'mrr',
  'arr',
  'settled_vs_outstanding',
  'ar_aging_total',
  'refunds_total',
  'discount_impact',
  'commission_earned',
  'subscription_revenue',
  'placement_revenue',
  'tournament_revenue',
  'admin_credits',
] as const;
export type FinanceKpiKey = (typeof FINANCE_KPI_KEYS)[number];

/** Settlement status — mirrors `commission_statements.status` (read-only here). */
export const FINANCE_STATEMENT_STATUSES = ['draft', 'issued', 'settled', 'void'] as const;
export type FinanceStatementStatus = (typeof FINANCE_STATEMENT_STATUSES)[number];

/** The 4 exportable reports + 2 formats (D5). */
export const FINANCE_EXPORT_REPORTS = ['summary', 'settlements', 'refunds', 'receivables'] as const;
export type FinanceExportReport = (typeof FINANCE_EXPORT_REPORTS)[number];
export const FINANCE_EXPORT_FORMATS = ['csv', 'xlsx'] as const;
export type FinanceExportFormat = (typeof FINANCE_EXPORT_FORMATS)[number];
export const FINANCE_EXPORT_STATUSES = ['pending', 'processing', 'ready', 'failed'] as const;
export type FinanceExportStatus = (typeof FINANCE_EXPORT_STATUSES)[number];

// ─── Constants ───────────────────────────────────────────────────────────────

/** Day-boundary + period timezone (D3) — Lebanon-only at MVP. */
export const FINANCE_TIMEZONE = 'Asia/Beirut' as const;
/** Trailing window the hourly rollup recomputes (absorbs late cross-period reversals). */
export const FINANCE_ROLLUP_TRAILING_DAYS = 35 as const;
/** Manual-refresh rate limit (1 per this window per admin). */
export const FINANCE_REFRESH_MIN_INTERVAL_MS = 5 * 60 * 1000;
/** Export download signed-URL TTL (D5). */
export const FINANCE_EXPORT_URL_TTL_SECONDS = 24 * 60 * 60;
/** Top-N row count per entity kind (D10). */
export const FINANCE_TOP_N = 10 as const;

// ─── Read shapes ─────────────────────────────────────────────────────────────

/** Offset-paginated list envelope shared by the settlements + refunds + export-history endpoints. */
export type FinancePaginated<T> = {
  readonly data: readonly T[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
};

/**
 * One KPI card. `amountMinor` is the current-period value. When the
 * compare-to-previous toggle is on, `previousMinor` + `deltaPercent` are
 * populated. `informationalOnly` marks the 3 context cards (refunds/discount/
 * admin-credits) that are NOT part of the net bridge (D8). `breakdown` carries
 * the per-tier / per-type / per-kind split for the cards that have one
 * (subscription_revenue by tier, placement_revenue by type, commission_earned
 * split venue/trainer/tournament_listing).
 */
export type FinanceKpiCard = {
  readonly key: FinanceKpiKey;
  readonly amountMinor: number;
  readonly currency: 'USD';
  readonly previousMinor?: number;
  readonly deltaPercent?: number;
  readonly informationalOnly: boolean;
  readonly breakdown?: readonly { readonly label: string; readonly amountMinor: number }[];
};

export type FinanceTrendPoint = {
  /** ISO 8601 date (Asia/Beirut day). */
  readonly day: string;
  readonly grossMinor: number;
  readonly netMinor: number;
};

export type FinanceBySourceRow = {
  readonly source: FinanceRevenueSource;
  readonly grossMinor: number;
  readonly reversalMinor: number;
  readonly feeMinor: number;
  readonly netMinor: number;
};

/** issued → settled → overdue funnel (counts + amounts in minor units). */
export type FinanceFunnel = {
  readonly issuedCount: number;
  readonly issuedMinor: number;
  readonly settledCount: number;
  readonly settledMinor: number;
  readonly overdueCount: number;
  readonly overdueMinor: number;
};

export type FinanceTopRow = {
  readonly entityKind: FinanceEntityKind;
  readonly entityId: string;
  readonly entityName: string;
  readonly netMinor: number;
  /** Deep-link into the owning admin surface (F42 venue / F43 user / F41 tournament). */
  readonly deepLinkPath: string;
};

/** `GET /admin/financial/summary` response. */
export type FinanceSummary = {
  readonly period: { readonly from: string; readonly to: string };
  readonly compareTo?: { readonly from: string; readonly to: string };
  readonly kpis: readonly FinanceKpiCard[];
  readonly trend: readonly FinanceTrendPoint[];
  readonly bySource: readonly FinanceBySourceRow[];
  readonly funnel: FinanceFunnel;
  readonly topVenues: readonly FinanceTopRow[];
  readonly topTrainers: readonly FinanceTopRow[];
  readonly topTournaments: readonly FinanceTopRow[];
  /** `finance_worker_state.last_rollup_at` — drives the "Last refreshed at" banner. */
  readonly lastRefreshedAt: string | null;
  /** Always true at MVP — drives the "Pre-tax" banner (D12). */
  readonly preTax: true;
};

/** One row of the settlements table (a `commission_statements` projection). */
export type FinanceSettlementRow = {
  readonly statementId: string;
  readonly source: FinanceRevenueSource;
  readonly entityKind: FinanceEntityKind;
  readonly entityId: string;
  readonly entityName: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: FinanceStatementStatus;
  /** The owed total for this statement (commission for commission sources; fee for fee sources). */
  readonly amountMinor: number;
  readonly currency: 'USD';
  readonly issuedAt: string | null;
  readonly settledAt: string | null;
  readonly dueAt: string | null;
  /** Aging bucket — only for status='issued' (else null). */
  readonly agingBucket: ArAgingBucket | null;
  /** True iff status='issued' (the "needs reconciliation" filter). */
  readonly needsReconciliation: boolean;
  /** Deep-link → `/admin/commission-statements/:statementId` (F23 mark-settled). */
  readonly deepLinkPath: string;
};

/** One row of the consolidated refunds/reversals queue (D7). */
export type FinanceRefundRow = {
  readonly id: string;
  readonly occurredAt: string;
  readonly source: RefundSourceKind;
  readonly entityName: string;
  /** The originating invoice/statement/booking id (for cross-reference). */
  readonly originalInvoiceId: string | null;
  readonly amountMinor: number;
  readonly currency: 'USD';
  /** Source-specific status string (e.g. trigger_event / payment_status). */
  readonly status: string;
  /** Deep-link into the owning admin surface (D10/§10 of the feature plan). */
  readonly deepLinkPath: string;
};

export type FinanceReceivableRow = {
  readonly statementId: string;
  readonly source: FinanceRevenueSource;
  readonly entityKind: FinanceEntityKind;
  readonly entityId: string;
  readonly entityName: string;
  readonly amountMinor: number;
  readonly currency: 'USD';
  readonly dueAt: string | null;
  readonly daysOverdue: number;
  readonly bucket: ArAgingBucket;
  readonly deepLinkPath: string;
};

/** `GET /admin/financial/receivables` response (live AR aging, D9). */
export type FinanceReceivables = {
  readonly asOf: string;
  readonly buckets: readonly {
    readonly bucket: ArAgingBucket;
    readonly amountMinor: number;
    readonly statementCount: number;
  }[];
  readonly rows: readonly FinanceReceivableRow[];
};

/** One row of the export-history sub-tab + the POST/GET export response. */
export type FinanceExportJob = {
  readonly id: string;
  readonly requestedByAdminId: string | null;
  readonly requestedByAdminDisplayName: string | null;
  readonly report: FinanceExportReport;
  readonly format: FinanceExportFormat;
  readonly filters: Readonly<Record<string, unknown>>;
  readonly status: FinanceExportStatus;
  readonly rowCount: number | null;
  readonly fileHash: string | null;
  readonly createdAt: string;
  readonly completedAt: string | null;
  /** Freshly-minted 24h signed URL when status='ready' (never persisted). */
  readonly downloadUrl: string | null;
  readonly expiresAt: string | null;
};

/** One configurable alert rule (`GET /admin/financial/alert-rules`). */
export type FinanceAlertRule = {
  readonly ruleType: FinanceAlertType;
  readonly enabled: boolean;
  readonly thresholdMinor: number | null;
  readonly thresholdDays: number | null;
  readonly summaryHour: number | null;
  readonly digestDow: number | null;
  readonly lastCheckedAt: string | null;
  readonly lastFiredAt: string | null;
  readonly updatedAt: string;
};

/** `POST /admin/financial/refresh` response. */
export type FinanceRefreshResult = {
  readonly lastRefreshedAt: string;
};

// ─── Zod input schemas ───────────────────────────────────────────────────────

/** ISO `YYYY-MM-DD` calendar date (Asia/Beirut). */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const sourceEnum = z.enum(FINANCE_REVENUE_SOURCES);
const agingEnum = z.enum(AR_AGING_BUCKETS);
const statementStatusEnum = z.enum(FINANCE_STATEMENT_STATUSES);

/** `GET /admin/financial/summary?from&to&compare&source` */
export const FinanceSummaryQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
    compare: queryBoolean().optional().default(false),
    source: sourceEnum.optional(),
  })
  .strict();
export type FinanceSummaryQuery = z.infer<typeof FinanceSummaryQuerySchema>;

/** `GET /admin/financial/settlements?...` */
export const FinanceSettlementsQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
    source: sourceEnum.optional(),
    status: statementStatusEnum.optional(),
    aging: agingEnum.optional(),
    entityId: z.string().uuid().optional(),
    needsReconciliation: queryBoolean().optional(),
    page: z.coerce.number().int().min(1).optional().default(1),
    perPage: z.coerce.number().int().min(1).max(100).optional().default(50),
  })
  .strict();
export type FinanceSettlementsQuery = z.infer<typeof FinanceSettlementsQuerySchema>;

/** `GET /admin/financial/refunds?...` */
export const FinanceRefundsQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
    source: z.enum(REFUND_SOURCE_KINDS).optional(),
    page: z.coerce.number().int().min(1).optional().default(1),
    perPage: z.coerce.number().int().min(1).max(100).optional().default(50),
  })
  .strict();
export type FinanceRefundsQuery = z.infer<typeof FinanceRefundsQuerySchema>;

/** `GET /admin/financial/receivables?asOf` */
export const FinanceReceivablesQuerySchema = z
  .object({
    asOf: isoDate.optional(),
  })
  .strict();
export type FinanceReceivablesQuery = z.infer<typeof FinanceReceivablesQuerySchema>;

/** `POST /admin/financial/exports` body. `filters` mirrors the report's list query. */
export const GenerateFinanceExportInputSchema = z
  .object({
    report: z.enum(FINANCE_EXPORT_REPORTS),
    format: z.enum(FINANCE_EXPORT_FORMATS),
    from: isoDate,
    to: isoDate,
    filters: z
      .object({
        source: sourceEnum.optional(),
        status: statementStatusEnum.optional(),
        aging: agingEnum.optional(),
        entityId: z.string().uuid().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type GenerateFinanceExportInput = z.infer<typeof GenerateFinanceExportInputSchema>;

/** `PATCH /admin/financial/alert-rules/:ruleType` body (partial update). */
export const UpdateFinanceAlertRuleInputSchema = z
  .object({
    enabled: z.boolean().optional(),
    thresholdMinor: z.number().int().min(0).nullable().optional(),
    thresholdDays: z.number().int().min(0).nullable().optional(),
    summaryHour: z.number().int().min(0).max(23).nullable().optional(),
    digestDow: z.number().int().min(0).max(6).nullable().optional(),
  })
  .strict();
export type UpdateFinanceAlertRuleInput = z.infer<typeof UpdateFinanceAlertRuleInputSchema>;

// ─── Error codes ─────────────────────────────────────────────────────────────

/**
 * F44 error vocabulary. Service maps to HTTP:
 *   - `FINANCE_RANGE_INVALID` / `FINANCE_ALERT_RULE_INVALID` → 422
 *   - `FINANCE_EXPORT_NOT_FOUND` → 404
 *   - `FINANCE_EXPORT_EXPIRED` → 410
 *   - `FINANCE_REFRESH_RATE_LIMITED` → 429
 *   - `FINANCE_EXPORT_FAILED` → 500
 */
export const FINANCE_ERROR_CODES = [
  'FINANCE_RANGE_INVALID',
  'FINANCE_EXPORT_NOT_FOUND',
  'FINANCE_EXPORT_EXPIRED',
  'FINANCE_EXPORT_FAILED',
  'FINANCE_REFRESH_RATE_LIMITED',
  'FINANCE_ALERT_RULE_INVALID',
] as const;
export type FinanceErrorCode = (typeof FINANCE_ERROR_CODES)[number];
